import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

const short = (v) => {
  if (v === null || v === undefined) return "—";
  const s = typeof v === "string" ? v : JSON.stringify(v);
  return s.length > 90 ? `${s.slice(0, 90)}…` : s;
};

// Shows the result of a dry run: errors (with JSON paths), warnings and a diff.
// Saves only when the user confirms and there are no errors.
export default function ReviewDialog({ open, onClose, title, result, onConfirm, busy, confirmText = "Confirm and save" }) {
  const errors = result?.errors || [];
  const warnings = result?.warnings || [];
  const changes = result?.changes || [];
  const extra = result?.assignments || [];           // apply-therapy preview
  const settingsChanges = result?.settings || [];
  const total = changes.length + settingsChanges.length + extra.reduce((n, a) => n + (a.changes?.length || 0), 0);
  const needsOlder = result?.requires_overwrite_confirmation;
  const canSave = errors.length === 0 && (total > 0 || extra.length > 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="review-dialog">
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>

        {errors.length > 0 && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid="review-errors">
            <div className="flex items-center gap-2 text-sm font-medium text-red-800 mb-2">
              <XCircle className="h-4 w-4" /> {errors.length} problem{errors.length > 1 ? "s" : ""} — nothing will be saved
            </div>
            <ul className="text-xs text-red-800 space-y-1 font-mono">
              {errors.map((e, i) => <li key={i}><b>{e.path || "(root)"}</b> {e.message}</li>)}
            </ul>
          </div>
        )}

        {warnings.map((w, i) => (
          <div key={i} className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5" /> {w}
          </div>
        ))}
        {needsOlder && (
          <div className="text-sm text-amber-900">Confirming will <b>overwrite with an older version</b>.</div>
        )}

        {errors.length === 0 && (
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-emerald-800 mb-2">
              <CheckCircle2 className="h-4 w-4" /> {total === 0 && extra.length === 0 ? "No changes" : `${total} change${total === 1 ? "" : "s"}`}
            </div>
            <DiffList title={extra.length || settingsChanges.length ? "Patient settings" : null} changes={changes.length ? changes : settingsChanges} />
            {extra.map((a, i) => (
              <div key={i} className="mt-3">
                <div className="text-xs uppercase tracking-[0.12em] text-purple-700">
                  {a.action === "create" ? "Add" : "Update"} module · {a.simulation || a.module}
                </div>
                <DiffList changes={a.changes} />
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={onConfirm} disabled={!canSave || busy} className="btn-brand rounded-full" data-testid="review-confirm">
            {needsOlder ? "Overwrite with older version" : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DiffList({ changes, title }) {
  if (!changes || changes.length === 0) return null;
  return (
    <div className="mt-1">
      {title && <div className="text-xs uppercase tracking-[0.12em] text-purple-700 mb-1">{title}</div>}
      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-72 overflow-y-auto">
        {changes.slice(0, 200).map((c, i) => (
          <div key={i} className="px-3 py-1.5 text-xs font-mono grid grid-cols-12 gap-2">
            <div className="col-span-5 truncate text-slate-700" title={c.path}>{c.path}</div>
            <div className="col-span-3 truncate text-red-700 line-through" title={short(c.before)}>{short(c.before)}</div>
            <div className="col-span-4 truncate text-emerald-700" title={short(c.after)}>{short(c.after)}</div>
          </div>
        ))}
        {changes.length > 200 && <div className="px-3 py-1.5 text-xs text-slate-500">…and {changes.length - 200} more</div>}
      </div>
    </div>
  );
}
